import axios from "axios";
import { serviceConsumer } from "../network/ServiceConsumer";

// Adjudicator accounts (MC2 card 930d3ac0). Every adjudicator gets their own
// Feis.link account: a booked judge is emailed a personal link to
// /adjudicator/invite/<token>, accepts it with a new account or by signing in
// to the parent/teacher account that email already has, and adds the photo
// shown with their (CLRG directory) name on event pages and results.

const base = () => process.env.REACT_APP_BASE_URL;

export const getInvitation = (token) =>
  serviceConsumer("GET", `${base()}/adjudicator/invite/${encodeURIComponent(token)}`);

// Requests carrying a password go through axios directly, as SignIn does:
// ServiceConsumer trims every string, which would change a password.
const postWithPassword = async (url, body) => {
  try {
    const res = await axios.post(url, body, {
      headers: {
        Authorization: "Basic " + window.btoa(`${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`),
      },
    });
    return res.data;
  } catch (err) {
    throw refusal(err?.response?.data?.message || "ServerError");
  }
};

export const acceptWithNewAccount = (token, body) =>
  postWithPassword(`${base()}/adjudicator/invite/${encodeURIComponent(token)}/accept`, body);

export const signIn = (email, password) => postWithPassword(`${base()}/login`, { email, password });

// The global axios interceptor leaves these to the invitation page.
export const isAdjudicatorInviteRequest = (error) =>
  /\/adjudicator\/invite\//.test(String(error?.config?.url || ""));

export const claimWithAccount = (token, body = {}) =>
  serviceConsumer("POST", `${base()}/adjudicator/invite/${encodeURIComponent(token)}/claim`, body);

export const getProfile = () => serviceConsumer("GET", `${base()}/adjudicator/profile`);

export const saveProfile = (body) => serviceConsumer("PUT", `${base()}/adjudicator/profile`, body);

export const uploadPhoto = (file) => {
  const formData = new FormData();
  formData.append("image", file, "photo.jpg");
  return serviceConsumer("POST", `${base()}/adjudicator/photo`, formData);
};

// Upload a squared photo, then make it the profile photo.
export const savePhoto = async (blob, extra = {}) => {
  const res = await uploadPhoto(blob);
  if (!res?.url) throw refusal("The photo didn't upload. Please try again.");
  return saveProfile({ photo: res.url, ...extra });
};

// The name a judge chose with the pencil, as the API takes it (only when they
// changed it; Devin 2026-10-27).
export const withChosenName = (chosenName) => (chosenName ? { judgeName: chosenName } : {});

// An error shaped like ServiceConsumer's ({ messages }).
export const refusal = (message) => Object.assign(new Error(message), { messages: message });

// The server's own words, or a fallback (ServiceConsumer throws { messages }).
export const errorText = (err, fallback) =>
  typeof err?.messages === "string" && err.messages !== "ServerError" ? err.messages : fallback;

// Signed in on this browser (the same keys SignIn writes).
export const storeSession = (data) => {
  // a different account may have been signed in here (an organiser session's
  // markers would otherwise outlive it)
  localStorage.clear();
  const first = String(data.fname || "");
  localStorage.setItem("accessToken", data.accessToken);
  localStorage.setItem("userFirstName", first.charAt(0).toUpperCase() + first.slice(1));
  localStorage.setItem("isTeacher", data.teacher);
  // true after accepting; a plain sign-in reports the account's own flag and
  // the claim sets it once it succeeds
  localStorage.setItem("isAdjudicator", !!data.adjudicator);
};

export const isAdjudicator = () => localStorage.getItem("isAdjudicator") === "true";

// Photos go up as a 1024x1024 JPEG, the size every Feis.link portrait uses:
// the square the judge framed in the photo editor (PhotoEditor: drag to move,
// pinch or slide to zoom). `area` is the cropped square in the source image's
// pixels.
export const PHOTO_TYPES = /^image\/(jpeg|png)$/;

export const cropPhoto = (src, area, size = 1024) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("That photo couldn't be read."))),
        "image/jpeg",
        0.9,
      );
    };
    img.onerror = () => reject(new Error("That photo couldn't be read."));
    img.src = src;
  });
